import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  isElectron,
  isMacElectron,
  useLogout,
  useMe,
  useProjects,
  useWorkspace,
  useWorkspaces,
} from '@fluxter/shared';
import {
  BarChart3,
  Calendar as CalendarIcon,
  Check,
  ChevronsUpDown,
  Clock,
  KanbanSquare,
  LogOut,
  MoreHorizontal,
  Plus,
  Settings,
  StickyNote,
  Users,
} from 'lucide-react';
import { AppTopBar } from '@/components/AppTopBar';
import { CommandPaletteProvider } from '@/components/CommandPaletteProvider';
import { TopBarSlotsProvider } from '@/components/TopBarSlots';
import { NotificationsBell } from '@/components/NotificationsBell';
import { MemberAvatar } from '@/components/MemberAvatar';
import { useDesktopNotifications } from '@/lib/desktop-notifications';
import { cn } from '@/lib/utils';

/**
 * Pull the domain segment out of an email address. Returns empty
 * string when the email is missing or malformed — the caller can
 * then suppress the second user-card line cleanly.
 */
const extractDomain = (email: string | null | undefined): string => {
  if (!email) return '';
  const at = email.indexOf('@');
  if (at < 0 || at === email.length - 1) return '';
  return email.slice(at + 1);
};
import { tokenStore } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { FluxterMark } from '@/components/FluxterMark';
import { ProjectCreateModal } from '@/components/ProjectCreateModal';
import { WorkspaceCreateModal } from '@/components/WorkspaceCreateModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
export interface WorkspaceShellProps {
  workspaceSlug: string;
  children: ReactNode;
}

interface ProjectListItem {
  id: string;
  attributes?: { name?: string; slug?: string };
}

interface WorkspaceListItem {
  id: string;
  attributes?: { name?: string; slug?: string; image_url?: string | null };
}

/**
 * Workspace icon — custom logo (R2-served) when present, the default
 * FluxterMark otherwise. Rendered as `<img>` with `object-contain` so
 * non-square uploads letterbox cleanly inside the square slot the
 * sidebar reserves; alt text mirrors the workspace name for screen
 * readers. A subtle rounded background fills the slot when the logo
 * has transparent edges (PNG / WebP / SVG with no own backdrop).
 */
interface WorkspaceIconProps {
  imageUrl?: string | null;
  workspaceName?: string;
  /** Tailwind size classes for the rendered icon. */
  className?: string;
  /** Stroke weight to forward to the FluxterMark fallback. */
  fallbackStrokeWidth?: number;
}

const WorkspaceIcon = ({
  imageUrl,
  workspaceName,
  className,
  fallbackStrokeWidth = 3,
}: WorkspaceIconProps) => {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={workspaceName ? `${workspaceName} logo` : 'Workspace logo'}
        className={cn('shrink-0 rounded-md object-cover', className)}
      />
    );
  }
  return <FluxterMark className={cn('shrink-0', className)} strokeWidth={fallbackStrokeWidth} />;
};

export const WorkspaceShell = ({ workspaceSlug, children }: WorkspaceShellProps) => {
  // On macOS Electron, widen the collapsed icon-rail so the native
  // traffic-light cluster (~70px wide at our pinned position) fits
  // entirely within the sidebar — otherwise the rightmost button
  // crosses the vertical separator into the right pane. 5rem = 80px
  // is the tightest fit that still clears the green light without
  // letting it bleed into the inset.
  //
  // Web / Windows / Linux use the shadcn default (3rem / 48px) so the
  // browser build keeps its tighter rail. The override is plumbed via
  // SidebarProvider's `style` prop, which `--sidebar-width-icon` reads
  // from in the wrapper element.
  const sidebarStyleOverride: CSSProperties | undefined = isMacElectron()
    ? ({ '--sidebar-width-icon': '5rem' } as CSSProperties)
    : undefined;

  return (
    <SidebarProvider style={sidebarStyleOverride}>
      <WorkspaceSidebar workspaceSlug={workspaceSlug} />
      {/*
        Glass redesign — the SidebarInset receives the body gradient
        backdrop via inline style (Tailwind can't easily express a CSS
        variable that resolves to a `linear-gradient(...)` value).
        Keeps the gradient in one place: change `--shell-bg` in
        styles.css and the whole shell follows.

        `isolation: isolate` gives glass surfaces (cards, popovers,
        the topbar's backdrop-blur) a stacking context to compose
        within — without it, blur overflows leak into siblings and
        produce flickering during scroll.

        TopBarSlotsProvider wraps the inset so any descendant page
        can populate the persistent topbar's left/right slots via
        `useSetTopBarSlots()` — keeps the visual to one elevated
        header strip per app instead of the previous stacked-header
        pattern.

        CommandPaletteProvider wraps the same scope so ⌘K and the
        topbar Search button share one workspace-scoped palette
        instance. The palette body (with its background queries) is
        lazy + only mounts while open, so this layer is essentially
        free until the user actually triggers it.
      */}
      <SidebarInset
        // `min-w-0` lets the inset shrink past its intrinsic content
        // width when a descendant (e.g. the board's `min-w-max` grid)
        // would otherwise push the whole flex row wider than the
        // viewport. Without this, the topbar gets dragged sideways
        // alongside the columns.
        className="isolate min-w-0"
        style={{ background: 'var(--shell-bg)' }}
      >
        <CommandPaletteProvider workspaceSlug={workspaceSlug}>
          <TopBarSlotsProvider>
            <AppTopBar />
            {/*
              `overflow-x: hidden` on the content area is the inner
              half of the same containment — children are free to
              manage their own horizontal scroll (the board does), but
              it stays inside the content frame. `min-w-0` mirrors the
              inset's so flex sizing doesn't fight us.
            */}
            {/*
              `pt-6` matches the prototype's breathing room between
              the topbar's bottom edge and each page's H1 header.
              Edge-to-edge surfaces (Board / List / Calendar /
              TaskDetail) opt out via `-mx-6 -my-6` on their root
              wrapper to keep their flush layout.
            */}
            <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto px-6 pt-6 pb-6">
              {children}
            </div>
          </TopBarSlotsProvider>
        </CommandPaletteProvider>
      </SidebarInset>
    </SidebarProvider>
  );
};

const WorkspaceSidebar = ({ workspaceSlug }: { workspaceSlug: string }) => {
  const navigate = useNavigate();
  const me = useMe();
  const ws = useWorkspace(workspaceSlug);
  const projects = useProjects(workspaceSlug);
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const logout = useLogout(async () => {
    await tokenStore.clear();
    await navigate({ to: '/login' });
  });

  // Dock badge + native OS toast notifications. No-ops on web — the
  // hook checks `isElectron()` internally. Mounted here (vs. higher
  // up the tree) so it lives only while the user is signed in to a
  // workspace; logout unmounts it and clears the badge naturally on
  // the next sign-in's first poll.
  useDesktopNotifications();

  // Bridge the desktop application menu's "Sign Out" item into the
  // existing logout flow. The main process sends `fluxter:sign-out`
  // when the menu is clicked; here we route it through the same
  // mutation the sidebar's Sign Out button uses, so React Query
  // cache invalidation + token clear + navigation all stay in one
  // place. Web build skips registration entirely (the bridge is
  // undefined there).
  useEffect(() => {
    if (!isElectron()) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bridge = (window as any).fluxter as
      | { onSignOut?: (cb: () => void) => () => void }
      | undefined;
    if (!bridge?.onSignOut) return;
    return bridge.onSignOut(() => logout.mutate());
  }, [logout]);

  const wsAttributes = (
    ws.data as { data?: { attributes?: { name?: string; image_url?: string | null } } } | undefined
  )?.data?.attributes;
  const wsName = wsAttributes?.name;
  const wsImageUrl = wsAttributes?.image_url;
  const userAttrs = (
    me.data as { data?: { attributes?: { name?: string; email?: string } } } | undefined
  )?.data?.attributes;
  const projectList = (projects.data as { data?: ProjectListItem[] } | undefined)?.data ?? [];

  const settingsPath = `/w/${workspaceSlug}/settings`;
  const reportsPath = `/w/${workspaceSlug}/reports`;
  const notesPath = `/w/${workspaceSlug}/notes`;
  const peoplePath = `/w/${workspaceSlug}/people`;
  const isSettingsActive = currentPath === settingsPath;
  const isReportsActive = currentPath === reportsPath;
  const isNotesActive = currentPath === notesPath;
  const isPeopleActive = currentPath === peoplePath;

  // macOS Electron uses `titleBarStyle: 'hiddenInset'`, which keeps
  // the native traffic-light buttons but parks them in the top-left
  // corner of the window. Two pieces work together:
  //
  //   1. The collapsed icon-rail is widened to 5.5rem on macOS (set on
  //      `SidebarProvider` via `--sidebar-width-icon`) so the traffic
  //      lights fit horizontally inside the sidebar — they no longer
  //      overflow into the right pane.
  //   2. `pt-7` here pushes the workspace switcher button below the
  //      traffic-light strip vertically. Same value works for both
  //      expanded and collapsed states because in both cases the
  //      switcher icon ends up at roughly y=60, well clear of the
  //      lights at y=14–28.
  //
  // Outside macOS Electron the modifier collapses to zero — the rail
  // stays at the shadcn default 3rem and no top padding is reserved.
  const macTitleBarInset = isMacElectron();
  const headerDragStyle: CSSProperties = {
    // Sidebar header doubles as a window drag handle in Electron;
    // browsers ignore the property.
    WebkitAppRegion: 'drag',
  } as CSSProperties;
  const noDragStyle: CSSProperties = { WebkitAppRegion: 'no-drag' } as CSSProperties;

  // In the widened macOS rail, every `SidebarMenu` (a flex column)
  // gets `items-center` so its `<li>` children shrink to button width
  // and sit horizontally centered in the rail. Avoids the descendant
  // attribute-selector dance — `align-items` works regardless of how
  // shadcn's `asChild` rewrites individual buttons (`<button>` vs
  // `<a>`), so bell, workspace switcher, and footer nav all align
  // identically. Expanded mode is unaffected because each item still
  // stretches to full width via the menu's default flex behavior.
  const macSidebarMenuCenter = macTitleBarInset ? 'group-data-[collapsible=icon]:items-center' : '';

  return (
    <Sidebar collapsible="icon" data-shell-chrome>
      <SidebarHeader
        className={cn(
          // macOS Electron: top inset clears the traffic-light strip.
          // Web: tightened to 2px so the workspace switcher's text
          // baseline lines up with the topbar's breadcrumbs / page
          // title across the rail boundary. The shadcn primitive
          // ships `p-2` (8px all sides), which sat the switcher one
          // notch lower than the topbar text — small but visible
          // misalignment.
          macTitleBarInset && 'pt-8',
          !isElectron() && 'pt-0.5',
        )}
        style={headerDragStyle}
      >
        <SidebarMenu className={macSidebarMenuCenter}>
          <SidebarMenuItem style={noDragStyle}>
            <WorkspaceSwitcher
              currentSlug={workspaceSlug}
              currentName={wsName}
              currentImageUrl={wsImageUrl}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup className="group-data-[collapsible=icon]:hidden">
          {/*
            Prototype `.fx-sidebar-section-head`: 11px uppercase,
            letter-spacing 0.06em, foreground-faint colour, font 600.
            Override shadcn defaults via className.
          */}
          <SidebarGroupLabel className="text-foreground-faint text-[11px] font-semibold tracking-[0.06em] uppercase">
            Projects
          </SidebarGroupLabel>
          <SidebarGroupAction title="New project" onClick={() => setCreateProjectOpen(true)}>
            <Plus className="size-3.5" />
            <span className="sr-only">New project</span>
          </SidebarGroupAction>
          <SidebarGroupContent>
            <SidebarMenu className={macSidebarMenuCenter}>
              {projects.isLoading ? (
                <SidebarMenuItem>
                  <span className="text-muted-foreground px-2 py-1 text-xs">Loading…</span>
                </SidebarMenuItem>
              ) : projectList.length === 0 ? (
                <SidebarMenuItem>
                  <span className="text-muted-foreground px-2 py-1 text-xs italic">
                    No projects yet.
                  </span>
                </SidebarMenuItem>
              ) : (
                projectList.map((p) => {
                  const slug = p.attributes?.slug ?? '';
                  const projectName = p.attributes?.name ?? 'Untitled';
                  const path = `/w/${workspaceSlug}/p/${slug}`;
                  // Active when the user is on any per-project surface
                  // (list / board / calendar / time / notes / settings).
                  // Previously only the list view lit up, which made it
                  // unclear which project context you were in when on
                  // a sub-view.
                  const active = currentPath.startsWith(`${path}`);
                  return (
                    <SidebarMenuItem key={p.id}>
                      <SidebarMenuButton asChild isActive={active} tooltip={projectName}>
                        <Link
                          to="/w/$workspaceSlug/p/$projectSlug"
                          params={{ workspaceSlug, projectSlug: slug }}
                        >
                          {/*
                            Prototype `.fx-sidebar-dot`: 6×6, brand
                            orange, opacity 0.5 inactive / 1 active.
                            Same colour for every project — the
                            opacity drives the active state, not the
                            colour family.
                          */}
                          <span
                            aria-hidden="true"
                            className={cn(
                              'h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--brand-accent)] transition-opacity',
                              active ? 'opacity-100' : 'opacity-50',
                            )}
                          />
                          <span className="truncate">{projectName}</span>
                        </Link>
                      </SidebarMenuButton>
                      {/*
                        Per-project quick-jump menu (slice 9c polish).
                        Hover-revealed `…` on each project lets the
                        user jump to Board / Calendar / Time / Notes /
                        Settings without first navigating to the
                        project's list view. Settings was previously
                        only reachable via the gear icon in the
                        project detail header — this surfaces it from
                        anywhere.
                      */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <SidebarMenuAction showOnHover aria-label={`More for ${projectName}`}>
                            <MoreHorizontal className="size-4" />
                          </SidebarMenuAction>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="start">
                          <DropdownMenuItem asChild>
                            <Link
                              to="/w/$workspaceSlug/p/$projectSlug/board"
                              params={{ workspaceSlug, projectSlug: slug }}
                            >
                              <KanbanSquare className="size-4" />
                              Board
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link
                              to="/w/$workspaceSlug/p/$projectSlug/calendar"
                              params={{ workspaceSlug, projectSlug: slug }}
                            >
                              <CalendarIcon className="size-4" />
                              Calendar
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link
                              to="/w/$workspaceSlug/p/$projectSlug/time"
                              params={{ workspaceSlug, projectSlug: slug }}
                            >
                              <Clock className="size-4" />
                              Time
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link
                              to="/w/$workspaceSlug/p/$projectSlug/notes"
                              params={{ workspaceSlug, projectSlug: slug }}
                            >
                              <StickyNote className="size-4" />
                              Notes
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link
                              to="/w/$workspaceSlug/p/$projectSlug/settings"
                              params={{ workspaceSlug, projectSlug: slug }}
                            >
                              <Settings className="size-4" />
                              Settings
                            </Link>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuItem>
                  );
                })
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu className={macSidebarMenuCenter}>
          {/*
            Notification bell sits at the top of the footer menu so
            it follows the same alignment + collapse behaviour as
            the rest of the items below. PopoverContent portals to
            body so it overlays the sidebar cleanly.
          */}
          <SidebarMenuItem>
            <NotificationsBell />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isNotesActive} tooltip="Notes">
              <Link to="/w/$workspaceSlug/notes" params={{ workspaceSlug }}>
                <StickyNote className="size-4" />
                <span>Notes</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isPeopleActive} tooltip="People">
              <Link to="/w/$workspaceSlug/people" params={{ workspaceSlug }}>
                <Users className="size-4" />
                <span>People</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isReportsActive} tooltip="Reports">
              <Link to="/w/$workspaceSlug/reports" params={{ workspaceSlug }}>
                <BarChart3 className="size-4" />
                <span>Reports</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={isSettingsActive} tooltip="Settings">
              <Link to="/w/$workspaceSlug/settings" params={{ workspaceSlug }}>
                <Settings className="size-4" />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        <SidebarSeparator />

        {/*
          Prototype `.fx-sidebar-user`: 26px avatar + name + sub line +
          icon-only sign-out on the right. Two text lines:
            - Top: display name OR email (whichever's available)
            - Sub: email if a display name took the top line; otherwise
                   the email's domain (mirrors the prototype's
                   "rodolfo@13bytes.co" / "13bytes.co" pattern)

          Avatar size 26 doesn't match MemberAvatar's `size` enum
          exactly; override via `!h-[26px] !w-[26px]` so dimensions
          match the prototype precisely without expanding the API.
        */}
        <div className="flex items-center gap-2.5 px-2 py-2 group-data-[collapsible=icon]:hidden">
          <MemberAvatar
            id={(me.data as { data?: { id?: string } } | undefined)?.data?.id ?? ''}
            name={userAttrs?.name ?? null}
            email={userAttrs?.email ?? null}
            size="md"
            className="!h-[26px] !w-[26px] !text-[10px]"
          />
          <div className="min-w-0 flex-1">
            <p className="text-foreground truncate text-[12.5px] font-medium leading-tight">
              {userAttrs?.name ?? userAttrs?.email ?? 'You'}
            </p>
            <p className="text-foreground-faint truncate text-[11px] leading-tight">
              {userAttrs?.name && userAttrs.email
                ? userAttrs.email
                : extractDomain(userAttrs?.email)}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => logout.mutate()}
            aria-label="Sign out"
            className="text-foreground-muted hover:text-foreground h-7 w-7 shrink-0 p-0"
          >
            <LogOut className="size-3.5" />
            <span className="sr-only">Sign out</span>
          </Button>
        </div>

        {/* Collapsed-state sign out — icon-only button. `flex` (not
            `block`) in the visible state so `items-center` from
            `macSidebarMenuCenter` actually has a flex axis to act on. */}
        <SidebarMenu
          className={cn('hidden group-data-[collapsible=icon]:flex', macSidebarMenuCenter)}
        >
          <SidebarMenuItem>
            <SidebarMenuButton tooltip="Sign out" onClick={() => logout.mutate()}>
              <LogOut className="size-4" />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <ProjectCreateModal
        workspaceSlug={workspaceSlug}
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
      />
    </Sidebar>
  );
};

interface WorkspaceSwitcherProps {
  currentSlug: string;
  currentName: string | undefined;
  currentImageUrl: string | null | undefined;
}

/**
 * Sidebar header dropdown. Lists all workspaces the user belongs to and exposes
 * the "+ Create workspace" action. The whole brand-mark + name area is the
 * trigger; in collapsed mode just the icon is shown and the menu flies out
 * to the right.
 */
const WorkspaceSwitcher = ({
  currentSlug,
  currentName,
  currentImageUrl,
}: WorkspaceSwitcherProps) => {
  const navigate = useNavigate();
  const workspaces = useWorkspaces();
  const { isMobile } = useSidebar();
  const [createOpen, setCreateOpen] = useState(false);

  const items = (workspaces.data as { data?: WorkspaceListItem[] } | undefined)?.data ?? [];

  const handleSwitch = (slug: string) => {
    // Clicking the current workspace navigates to its home (project grid view) —
    // useful since the brand-name area in the sidebar header is now a dropdown
    // trigger, no longer a direct link to home.
    void navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug: slug } });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            tooltip={currentName ?? 'Workspace'}
            // `justify-center` in collapsed mode centers the FluxterMark
            // (20px) within the 32px button — without it the icon is at
            // flex-start and reads as left-shifted, especially when the
            // hover background sits cleanly centered around it.
            className="data-[slot=sidebar-menu-button]:!p-2 data-[state=open]:bg-sidebar-accent group-data-[collapsible=icon]:justify-center"
          >
            <WorkspaceIcon
              imageUrl={currentImageUrl}
              workspaceName={currentName}
              className="!h-5 !w-5"
            />
            {/* Hide text + chevron in collapsed mode so they don't take
                flex axis space and pull the icon off-center. Both are
                clipped by overflow-hidden anyway, so removing them only
                affects layout, not visible content. */}
            <div className="flex min-w-0 flex-col gap-0.5 leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate text-sm font-semibold">{currentName ?? 'Workspace'}</span>
            </div>
            <ChevronsUpDown className="ml-auto size-4 opacity-60 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="min-w-56 rounded-md"
          align="start"
          side={isMobile ? 'bottom' : 'right'}
          sideOffset={4}
        >
          <DropdownMenuLabel className="text-muted-foreground text-xs">
            Workspaces
          </DropdownMenuLabel>
          {items.map((w) => {
            const slug = w.attributes?.slug ?? '';
            const isCurrent = slug === currentSlug;
            return (
              <DropdownMenuItem key={w.id} onClick={() => handleSwitch(slug)} className="gap-2">
                <WorkspaceIcon
                  imageUrl={w.attributes?.image_url}
                  workspaceName={w.attributes?.name}
                  className="!h-3.5 !w-3.5"
                />
                <span className="truncate">{w.attributes?.name ?? 'Untitled'}</span>
                {isCurrent ? <Check className="ml-auto size-4" /> : null}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="size-4" />
            Create workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <WorkspaceCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
};
